import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal, Spinner, Tabs, Tab } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchDeployConfig,
  fetchDeployHistory,
  fetchDeployImages,
  fetchDeployLog,
  deployRun,
  deployRunGit,
  deploySwitch,
  saveDeployConfig,
} from "../../redux/actionCreator";
import { AlertTriangle, Package, Compass, Scroll, Check, X, Wand2} from "lucide-react";
import DeployWizard from "./DeployWizard";
import ApplicationsManager from "./ApplicationsManager";
import ClusterPanel from "./ClusterPanel";
import "./deployPage.scss";

const RESTART_POLICIES = ["no", "on-failure", "always", "unless-stopped"];

const blank = {
  enabled: false,
  mode: "git_build",
  container_name: "",
  image_ref: "",
  git_url: "",
  git_branch: "main",
  git_username: "",
  has_git_password: false,
  repo_dir: "",
  keep_versions: 3,
  port_mappings: [],
  env_vars: [],
  env_file_path: "",
  volumes: [],
  network: "bridge",
  restart_policy: "unless-stopped",
  extra_args: "",
  upload_dir: "/tmp/smarthatch-deploys",
  target_server_ids: [],
};

/**
 * Project-level Docker deploy page.
 *
 * Three panels:
 *  1. Config — container name, image ref, ports/volumes/env, target servers
 *  2. Run    — file picker for the .tar from `docker save`, upload + run
 *  3. History — last N deploys with per-server status
 *
 * The Run panel uses whatever the saved config holds; users can override the
 * server selection per-deploy without touching the saved targets. Image-ref
 * is parsed from `docker load` output server-side if not pinned in config.
 */
export default function DeployPage({ projectId: projectIdProp }) {
  const dispatch = useDispatch();
  const params = useParams();
  // Reachable two ways: the route `/deploy/:projectId`, or embedded in the
  // sidebar Deploy page which passes the picked project as a prop.
  const projectId = projectIdProp || params.projectId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [servers, setServers] = useState([]);
  const [cfg, setCfg] = useState(blank);
  const [originalCfg, setOriginalCfg] = useState(blank);
  const [err, setErr] = useState(null);
  // Top-of-page tab: Applications (the apps cards + add) vs Cluster (live
  // k8s view). The legacy single-config + history panels stay below both.
  const [topTab, setTopTab] = useState("applications");
  const [showLegacyHistory, setShowLegacyHistory] = useState(false);
  // Mirrored from ApplicationsManager via onAppsChange so the env switcher
  // can scope BOTH Cluster (which servers are referenced by apps for this
  // stage) and Activity (which history rows belong to this stage) using
  // ONE source of truth — no duplicate fetch.
  const [allApps, setAllApps] = useState([]);
  // Page-level environment filter. Sticky per-project in localStorage so a
  // reload / context switch doesn't silently shift you between dev and prod.
  // Every sub-tab (Applications / Cluster / Activity) reads this and filters
  // its own scope accordingly: deploy actions run against this env's merged
  // config; the Activity table hides entries from other envs.
  const [environment, setEnvironment] = useState(() => {
    try { return localStorage.getItem(`sht-deploy-env-${projectId}`) || "development"; }
    catch { return "development"; }
  });
  useEffect(() => {
    try { localStorage.setItem(`sht-deploy-env-${projectId}`, environment); } catch {}
  }, [environment, projectId]);
  // Theme-aware: `color` reads a custom property that flips with the theme,
  // and `bg` is a translucent tint of the same hue so it works on a dark
  // surface as well as a light one. The old fixed pastels (#dbeafe etc.)
  // rendered as glowing white-blue blocks on the dark console.
  const ENV_META = {
    development: { label: "Development", color: "var(--info)",   bg: "rgba(76, 141, 255, 0.12)" },
    staging:     { label: "Staging",     color: "var(--warn)",   bg: "rgba(245, 165, 36, 0.12)" },
    production:  { label: "Production",  color: "var(--danger)", bg: "rgba(239, 68, 68, 0.12)" },
  };

  const [showWizard, setShowWizard] = useState(false);
  const [gitPassword, setGitPassword] = useState(""); // write-only field — empty means "leave existing"
  const [file, setFile] = useState(null);
  const [overrideRef, setOverrideRef] = useState("");
  const [branchOverride, setBranchOverride] = useState("");
  const [refOverride, setRefOverride] = useState("");
  const [runIds, setRunIds] = useState([]); // checked targets for THIS run
  const [runMode, setRunMode] = useState("git_build"); // active tab in Run panel
  const [running, setRunning] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);

  const [imagesByServer, setImagesByServer] = useState([]); // for the rollback panel
  const [switching, setSwitching] = useState(null);          // image_ref currently being switched
  const [logModal, setLogModal] = useState(null);            // { loading, text, meta }

  const fileInputRef = useRef(null);

  const loadImages = useCallback(async () => {
    if (!projectId) return;
    const res = await dispatch(fetchDeployImages(projectId));
    if (res?.status) setImagesByServer(res.data || []);
  }, [dispatch, projectId]);

  const loadAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setErr(null);
    const [confRes, hist] = await Promise.all([
      dispatch(fetchDeployConfig(projectId)),
      dispatch(fetchDeployHistory(projectId)),
    ]);
    setLoading(false);
    if (confRes?.status) {
      const next = { ...blank, ...(confRes.data.config || {}) };
      // Server returns ObjectIds — normalise to strings so checkbox compare works.
      next.target_server_ids = (next.target_server_ids || []).map(String);
      // env_vars docs may carry mongoose _id; strip those for the form state.
      next.env_vars = (next.env_vars || []).map((v) => ({ key: v.key || "", value: v.value || "" }));
      setCfg(next);
      setOriginalCfg(next);
      setServers(confRes.data.servers || []);
      setRunIds(next.target_server_ids);
      // Default the Run-panel tab to whatever the config says, so the user
      // lands on their usual flow.
      setRunMode(next.mode || "git_build");
      // Kick off the image list in the background — used by the rollback panel.
      loadImages();
    } else {
      setErr(confRes?.message || "Failed to load deploy config");
    }
    if (hist?.status) setHistory(hist.data || []);
  }, [dispatch, projectId, loadImages]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const dirty = useMemo(() => JSON.stringify(cfg) !== JSON.stringify(originalCfg), [cfg, originalCfg]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  // Generic edit-array helpers shared by ports/volumes/env-vars rows.
  const updateAt = (key, i, v) =>
    setCfg((c) => {
      const next = [...(c[key] || [])];
      next[i] = v;
      return { ...c, [key]: next };
    });
  const removeAt = (key, i) =>
    setCfg((c) => {
      const next = [...(c[key] || [])];
      next.splice(i, 1);
      return { ...c, [key]: next };
    });
  const addEntry = (key, blank) =>
    setCfg((c) => ({ ...c, [key]: [...(c[key] || []), blank] }));

  const toggleSavedTarget = (id) =>
    setCfg((c) => {
      const set = new Set((c.target_server_ids || []).map(String));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...c, target_server_ids: [...set] };
    });
  const toggleRunTarget = (id) => {
    setRunIds((curr) => {
      const set = new Set(curr.map(String));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    // Build payload: send git_password ONLY when the user typed something.
    // Empty string from a "PWD set" placeholder must NOT clear the stored
    // ciphertext — we use an explicit "__clear__" sentinel for that.
    const payload = { ...cfg };
    if (gitPassword) payload.git_password = gitPassword;
    const res = await dispatch(saveDeployConfig(projectId, payload));
    setSaving(false);
    if (res?.status) {
      const next = { ...blank, ...(res.data || {}) };
      next.target_server_ids = (next.target_server_ids || []).map(String);
      next.env_vars = (next.env_vars || []).map((v) => ({ key: v.key || "", value: v.value || "" }));
      setCfg(next);
      setOriginalCfg(next);
      setGitPassword("");
    } else {
      setErr(res?.message || "Save failed");
    }
  };

  const clearGitPassword = async () => {
    if (!window.confirm("Clear the saved Git password? You'll need to re-enter it before the next Git deploy.")) return;
    setSaving(true);
    const res = await dispatch(saveDeployConfig(projectId, { git_password: "__clear__" }));
    setSaving(false);
    if (res?.status) {
      setCfg((c) => ({ ...c, has_git_password: false }));
      setOriginalCfg((c) => ({ ...c, has_git_password: false }));
      setGitPassword("");
    } else {
      setErr(res?.message || "Failed to clear");
    }
  };

  const onPickFile = (f) => {
    if (!f) return;
    if (!/\.(tar|tar\.gz|tgz)$/i.test(f.name)) {
      if (!window.confirm(`${f.name} doesn't end in .tar — upload anyway?`)) return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragging");
    const f = e.dataTransfer.files?.[0];
    if (f) onPickFile(f);
  };

  const runDeployTar = async () => {
    if (!file) { setErr("Pick a .tar file first"); return; }
    if (!runIds.length) { setErr("Select at least one target server"); return; }
    if (!cfg.container_name) { setErr("container_name is required — set it in Config and Save."); return; }
    if (!cfg.enabled) { setErr("Enable Deploy in the Config panel first."); return; }
    setRunning(true);
    setUploadPct(0);
    setResults(null);
    setErr(null);
    const res = await dispatch(
      deployRun(
        projectId,
        file,
        { server_ids: runIds, image_ref_override: overrideRef || undefined },
        ({ percent }) => setUploadPct(percent),
      ),
    );
    setRunning(false);
    setUploadPct(null);
    if (res?.data?.results) {
      setResults(res.data.results);
      await refreshAfterRun();
    } else {
      setErr(res?.message || "Deploy failed");
    }
  };

  const runDeployGit = async () => {
    if (!runIds.length) { setErr("Select at least one target server"); return; }
    if (!cfg.container_name) { setErr("container_name is required — set it in Config and Save."); return; }
    if (!cfg.enabled) { setErr("Enable Deploy in the Config panel first."); return; }
    if (!cfg.git_url) { setErr("git_url is required — set it in Config and Save."); return; }
    if (!cfg.repo_dir) { setErr("repo_dir is required — set it in Config and Save."); return; }
    setRunning(true);
    setResults(null);
    setErr(null);
    const res = await dispatch(
      deployRunGit(projectId, {
        server_ids: runIds,
        branch_override: branchOverride || undefined,
        ref_override: refOverride || undefined,
      }),
    );
    setRunning(false);
    if (res?.data?.results) {
      setResults(res.data.results);
      await refreshAfterRun();
    } else {
      setErr(res?.message || "Git deploy failed");
    }
  };

  const refreshAfterRun = async () => {
    const [hist] = await Promise.all([
      dispatch(fetchDeployHistory(projectId)),
      loadImages(),
    ]);
    if (hist?.status) setHistory(hist.data || []);
  };

  const runSwitch = async (image_ref, serverId) => {
    if (!window.confirm(`Switch ${cfg.container_name} to ${image_ref} on the selected server?`)) return;
    setSwitching(`${serverId}:${image_ref}`);
    const res = await dispatch(deploySwitch(projectId, {
      image_ref,
      server_ids: [serverId],
    }));
    setSwitching(null);
    if (res?.data?.results) {
      setResults(res.data.results);
      await refreshAfterRun();
    } else {
      setErr(res?.message || "Switch failed");
    }
  };

  const openLog = async (idx, serverId) => {
    setLogModal({ loading: true, text: "", meta: null });
    const res = await dispatch(fetchDeployLog(projectId, idx, serverId));
    if (res?.status) {
      setLogModal({ loading: false, text: res.data?.log || "(empty)", meta: res.data });
    } else {
      setLogModal({ loading: false, text: res?.message || "Failed to load log", meta: null });
    }
  };

  const fmtSize = (n) => {
    if (!n) return "—";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
  };

  const serverById = useMemo(() => {
    const m = new Map();
    for (const s of servers) m.set(String(s._id), s);
    return m;
  }, [servers]);

  return (
    <div className="deploy-page">
      <div className="deploy-header">
        <div>
          <h3 className="m-0">Deploy</h3>
          <div className="text-muted small">Upload a Docker image tar and deploy to selected servers.</div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <div className="btn-group" role="group" aria-label="Environment">
            {Object.entries(ENV_META).map(([v, m]) => {
              const active = environment === v;
              return (
                <button
                  key={v}
                  type="button"
                  className={`btn btn-sm ${active ? "active" : ""}`}
                  onClick={() => setEnvironment(v)}
                  style={{
                    background: active ? m.color : "var(--bg-raised)",
                    color: active ? "#fff" : "var(--text-dim)",
                    borderColor: active ? m.color : "var(--line)",
                    fontWeight: active ? 600 : 400,
                  }}
                  title={`Switch to ${m.label}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowWizard(true)}>
            <Wand2 size={14} className="me-1" /> Guided deploy
          </Button>
          <Button variant="light" size="sm" onClick={loadAll} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻ Refresh"}
          </Button>
        </div>
      </div>
      <div
        className="d-flex justify-content-between align-items-center mt-2 mb-2 px-3 py-2 rounded"
        style={{ background: ENV_META[environment].bg, borderLeft: `4px solid ${ENV_META[environment].color}` }}
      >
        <div>
          <strong style={{ color: ENV_META[environment].color }}>{ENV_META[environment].label}</strong>{" "}
          <small className="text-muted">
            — Applications, Cluster, and Activity all filter to this environment. Switch any time.
          </small>
        </div>
        {environment === "production" && (
          <small className="text-danger fw-semibold d-inline-flex align-items-center gap-1"><AlertTriangle size={12} /> Actions go live in production</small>
        )}
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="deploy-toptabs">
        <button
          type="button"
          className={topTab === "applications" ? "on" : ""}
          onClick={() => setTopTab("applications")}
        >
          <Package size={14} className="me-1" /> Applications
        </button>
        <button
          type="button"
          className={topTab === "cluster" ? "on" : ""}
          onClick={() => setTopTab("cluster")}
        >
          <Compass size={14} className="me-1" /> Cluster
        </button>
        <button
          type="button"
          className={topTab === "activity" ? "on" : ""}
          onClick={() => setTopTab("activity")}
        >
          <Scroll size={14} className="me-1" /> Activity
        </button>
      </div>

      {topTab === "applications" && (
        <ApplicationsManager
          projectId={projectId}
          environment={environment}
          onEnvironmentChange={setEnvironment}
          onAppsChange={setAllApps}
          onActivity={async () => {
            // Refresh the activity tab after an app action (deploy / stop /
            // upload / rollback) so the table shows it on next view.
            const hist = await dispatch(fetchDeployHistory(projectId));
            if (hist?.status) setHistory(hist.data || []);
          }}
        />
      )}
      {topTab === "cluster" && (() => {
        // Servers used by THIS stage's apps. Union of target_server_ids
        // across every app whose `stage` matches the active env. Empty
        // = no apps on this stage have any deploys configured yet.
        const stageServerIds = new Set();
        for (const a of allApps) {
          if ((a.stage || "development") !== environment) continue;
          for (const sid of (a.target_server_ids || [])) stageServerIds.add(String(sid));
        }
        return (
          <ClusterPanel
            projectId={projectId}
            environment={environment}
            servers={servers}
            envServerIds={Array.from(stageServerIds)}
          />
        );
      })()}
      {topTab === "activity" && (
        <div className="panel">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="m-0 d-inline-flex align-items-center gap-2">
              <Scroll size={18} /> Recent deploys
              <span
                className="ms-2 badge"
                style={{
                  background: ENV_META[environment].bg,
                  color: ENV_META[environment].color,
                  fontWeight: 600,
                }}
              >
                {environment}
              </span>
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <Form.Check
                type="checkbox"
                id="show-legacy"
                checked={!!showLegacyHistory}
                onChange={(e) => setShowLegacyHistory(e.target.checked)}
                label={<small className="text-muted">Show legacy (no env tag)</small>}
              />
              <Button size="sm" variant="light" onClick={loadAll} disabled={loading}>
                {loading ? <Spinner size="sm" animation="border" /> : "↻ Refresh"}
              </Button>
            </div>
          </div>
          {(() => {
            // Filter to entries written FOR the active env. Older entries
            // (pre-feature) have no `environment` field — include them when
            // the operator opts in via the "Show legacy" toggle so old
            // deploys stay visible.
            const filtered = history.filter((h) => {
              const e = h.environment || "";
              if (!e) return showLegacyHistory;
              return e === environment;
            });
            const filteredCount = filtered.length;
            const hiddenCount = history.length - filteredCount;
            return (
              <>
                {hiddenCount > 0 && (
                  <small className="text-muted d-block mb-2">
                    {filteredCount} of {history.length} entries match{" "}
                    <strong>{environment}</strong> · {hiddenCount} from other envs / legacy are hidden.
                  </small>
                )}
                {filteredCount === 0 ? (
                  <div className="text-muted small">
                    No deploys to <strong>{environment}</strong> yet.
                    {!showLegacyHistory && history.length > 0 && (
                      <> Toggle "Show legacy" to see older entries without an environment tag.</>
                    )}
                  </div>
                ) : (
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Env</th>
                        <th>Source</th>
                        <th>Image</th>
                        <th>Results</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((h, i) => {
                  const ok = (h.results || []).filter((r) => r.status === "success").length;
                  const total = (h.results || []).length;
                  // Per-source subtitle + badge color so all action types read
                  // at a glance (build / upload / static / compose / kubectl /
                  // stop / remove / switch / rollback).
                  const SRC_BADGE = {
                    git_build: "primary",
                    tar_upload: "secondary",
                    static: "info",
                    compose: "dark",
                    kubectl: "dark",
                    switch: "warning",
                    stop: "danger",
                    remove: "danger",
                  };
                  const subtitle =
                    h.source === "git_build"
                      ? `${h.git_branch || ""}${h.git_sha ? "@" + h.git_sha : ""}`
                      : h.source === "switch"
                      ? "rollback"
                      : h.source === "static"
                      ? (h.image_filename
                          ? `${h.image_filename} · ${fmtSize(h.image_size)}`
                          : (h.image_ref || ""))
                      : h.source === "compose" || h.source === "kubectl"
                      ? `${h.action || "up"} · ${h.file || ""}`
                      : h.source === "stop" || h.source === "remove"
                      ? `container: ${h.image_ref || ""}`
                      : `${h.image_filename || ""} · ${fmtSize(h.image_size)}`;
                  return (
                    <tr key={i}>
                      <td>
                        <div>{new Date(h.at).toLocaleString()}</div>
                        <div className="text-muted" style={{ fontSize: 10 }}>{subtitle}</div>
                      </td>
                      <td>
                        {h.environment ? (
                          <span
                            className="badge"
                            style={{
                              background: ENV_META[h.environment]?.bg || "#e5e7eb",
                              color: ENV_META[h.environment]?.color || "#374151",
                              fontWeight: 600,
                            }}
                          >
                            {h.environment}
                          </span>
                        ) : (
                          <span className="badge bg-light text-muted" title="Deployed before env tagging">legacy</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${SRC_BADGE[h.source] || "secondary"}`}>
                          {h.source || "tar_upload"}
                        </span>
                      </td>
                      <td>
                        <code style={{ fontSize: 11 }}>{h.image_ref || "—"}</code>
                      </td>
                      <td>
                        <div>
                          <span className={ok === total ? "ok" : "bad"}>{ok}/{total}</span>{" "}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-1"
                            style={{ fontSize: 11 }}
                            onClick={() => openLog(i)}
                          >
                            view log
                          </button>
                        </div>
                        <div className="text-muted" style={{ fontSize: 10 }}>
                          {(h.results || []).map((r, j) => {
                            const s = serverById.get(String(r.server_id));
                            const label = s?.label || r.server_label || String(r.server_id);
                            return (
                              <React.Fragment key={j}>
                                {j > 0 && ", "}
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0"
                                  style={{ fontSize: 10 }}
                                  onClick={() => openLog(i, String(r.server_id))}
                                >
                                  <span className="d-inline-flex align-items-center gap-1">
                                    {r.status === "success" ? <Check size={10} /> : <X size={10} />} {label}
                                  </span>
                                </button>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                    </tbody>
                  </table>
                )}
              </>
            );
          })()}
        </div>
      )}

      <DeployWizard
        show={showWizard}
        onHide={() => setShowWizard(false)}
        projectId={projectId}
        servers={servers}
        initial={cfg}
        onDeployed={loadAll}
      />


      <Modal
        show={!!logModal}
        onHide={() => setLogModal(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <h5 className="m-0">Deploy log</h5>
        </Modal.Header>
        <Modal.Body>
          {logModal?.meta && (
            <div className="text-muted small mb-2">
              {new Date(logModal.meta.at).toLocaleString()}{" "}
              · <span className="badge bg-secondary">{logModal.meta.source}</span>
              {logModal.meta.image_ref && <> · <code>{logModal.meta.image_ref}</code></>}
            </div>
          )}
          {logModal?.loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <pre
              style={{
                background: "#0b1220",
                color: "#e2e8f0",
                fontSize: 11,
                padding: 12,
                borderRadius: 6,
                maxHeight: "60vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {logModal?.text}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setLogModal(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
